import { Injectable, inject } from '@angular/core';
import { App } from '@df/core/app';
import { Url } from '@df/core/service/url';
import { IResizeOptions, ResizeService } from '@df/ui/common/resize.service';
import defaults from 'lodash-es/defaults';
import { NThumbor } from './df-thumbor.interface';

@Injectable({
    providedIn: 'root'
})
export class ThumborResizeService extends ResizeService {
    private endpoint: string;
    private tcoProxy?: string;

    protected app = inject(App);
    protected url = inject(Url);
    protected thumborConfig = inject(NThumbor.OPTIONS);

    constructor() {
        super();
        const endpoint = this.app.dfConfig.thumbor.endpoint;
        this.endpoint = endpoint.match(/^http/) ? endpoint : '/' + endpoint;

        if (this.thumborConfig.useTcoProxy) {
            this.tcoProxy = this.app.dfConfig.endpoint.media + '/tco-images/o';
        }
    }

    /**
     * Image loader for `DfImageDirective`
     *
     * @param config {NThumbor.ILoaderConfig}
     * @returns {string}
     *
     * @link https://angular.dev/guide/image-optimization#configuring-an-image-loader-for-ngoptimizedimage
     */
    loader(config: NThumbor.ILoaderConfig): string {
        if (!config.src) {
            return '';
        }

        if (config.src?.match(/\.(svg|gif)$/)) {
            return this.url.toMediaUrl(config.src);
        }

        config.loaderParams = config.loaderParams || ({} as IResizeOptions);

        if (config.width) {
            config.loaderParams.width = config.width;
        }

        return this.resize(this.url.toMediaUrl(config.src), config.loaderParams);
    }

    resize(imageUrl: string, options: IResizeOptions): string {
        defaults(options, this.thumborConfig.defaultOptions);

        const args: string[] = [];
        const filters: string[] = [];

        let useProxy = false;

        if (this.tcoProxy) {
            const imageUrlParts = imageUrl.split('/static/');

            if (imageUrlParts.length > 1) {
                imageUrl = `static/${imageUrlParts[1]}`;
                useProxy = true;
            }
        }

        if (useProxy && this.tcoProxy) {
            args.push(this.tcoProxy);
        } else {
            args.push(this.endpoint);

            args.push('unsafe');
        }

        if (options?.trim && options.trim !== '0') {
            if ([1, true, 'true', '1'].indexOf(options.trim) !== -1) {
                args.push('trim');
            } else if (typeof options.trim === 'number' || (typeof options.trim === 'string' && options.trim.match(/^\d+$/))) {
                args.push('trim:top-left:' + options.trim);
            } else {
                args.push('trim:' + options.trim);
            }
        }

        if (options?.fitIn && options.fitIn !== '0') {
            switch (options.fitIn) {
                case 'adaptive':
                    args.push('adaptive-fit-in');
                    break;
                case 'full':
                    args.push('full-fit-in');
                    break;
                default:
                    args.push('fit-in');
            }
        }

        if (options?.width !== undefined && options?.height !== undefined) {
            args.push(Math.ceil(options.width) + 'x' + Math.ceil(options.height));
        } else if (options?.width !== undefined) {
            args.push(Math.ceil(options.width) + 'x0');
        }

        if (options?.horizontalCrop) {
            args.push(options.horizontalCrop);
        }

        if (options?.verticalCrop) {
            args.push(options.verticalCrop);
        }

        if (options?.cropType) {
            args.push(options.cropType);
        }

        if (options?.upscale) {
            filters.push('upscale()');
        }

        // filters
        if (options?.fillColor) {
            if (options.fillColor === 'transparent') {
                filters.push('fill(' + options.fillColor + ')');
                // fix for jpg on some phones
                filters.push('format(png)');
            } else {
                // fillFull is used to fill the whole image with the fill color including the existing part and not only added content
                if (options.fillFull) {
                    filters.push('fill(' + options.fillColor + ', 1)');
                } else {
                    filters.push('fill(' + options.fillColor + ')');
                }
            }
        }

        if (options?.sharpen) {
            filters.push('sharpen(' + options.sharpen + ')');
        }

        if (options?.format) {
            filters.push('format(' + options.format + ')');
        }

        if (options?.quality) {
            filters.push('quality(' + options.quality + ')');
        }

        if (filters.length) {
            args.push('filters:' + filters.join(':'));
        }

        // source
        args.push(imageUrl);

        return args.join('/');
    }
}
